import { Schema } from "effect";
import {
  ChannelThreadId,
  MessageId,
  SupportTicketId,
  TopicId,
} from "../../../ids/definitions";
import { CustomerBasicInfo } from "../../customer";
import { MessageChannel, MessageSenderType } from "./message";
import { SupportTicketStatus } from "./support-ticket";

/** Only non-closed handoff targets are surfaced here. */
export const TextConciergeEscalatedSupportTicket = Schema.Struct({
  id: SupportTicketId,
  status: SupportTicketStatus,
});
export type TextConciergeEscalatedSupportTicket =
  typeof TextConciergeEscalatedSupportTicket.Type;

/**
 * One row in the concierge conversations list. Backed by a
 * `text_concierge_conversation` row joined with its channel thread's messages
 * (counts + timestamps) and any open handoff to a support ticket.
 */
export const TextConciergeConversationListItem = Schema.Struct({
  topicId: TopicId,
  channelThreadId: ChannelThreadId,
  channel: MessageChannel,
  customer: Schema.NullOr(CustomerBasicInfo),
  numCustomerMessages: Schema.Number,
  numMerchantMessages: Schema.Number,
  lastCustomerMessageAt: Schema.NullOr(Schema.Date),
  lastMerchantMessageAt: Schema.NullOr(Schema.Date),
  lastCustomerMessageContent: Schema.NullOr(Schema.String),
  escalatedSupportTicket: Schema.NullOr(TextConciergeEscalatedSupportTicket),
  createdAt: Schema.Date,
  updatedAt: Schema.Date,
});
export type TextConciergeConversationListItem =
  typeof TextConciergeConversationListItem.Type;

export const TextConciergeConversationListResponse = Schema.Struct({
  items: Schema.Array(TextConciergeConversationListItem),
  hasMore: Schema.Boolean,
});
export type TextConciergeConversationListResponse =
  typeof TextConciergeConversationListResponse.Type;

export const TextConciergeFilterTab = Schema.Literal(
  "all",
  "awaiting",
  "replied",
  "escalated",
);
export type TextConciergeFilterTab = typeof TextConciergeFilterTab.Type;

/**
 * Slim message projection — bubble content, direction, timestamp, and
 * iMessage-vs-SMS styling cue only, so callers don't have to assemble the
 * full channel-specific Message union.
 */
export const TextConciergeMessage = Schema.Struct({
  id: MessageId,
  channelThreadId: ChannelThreadId,
  channel: MessageChannel,
  senderType: MessageSenderType,
  content: Schema.NullOr(Schema.String),
  sentAt: Schema.Date,
});
export type TextConciergeMessage = typeof TextConciergeMessage.Type;

/**
 * Detail-view response for a single concierge conversation. Works regardless
 * of whether the conversation has been escalated to a support ticket.
 */
export const TextConciergeConversationDetailResponse = Schema.Struct({
  topicId: TopicId,
  channelThreadId: ChannelThreadId,
  channel: MessageChannel,
  customer: Schema.NullOr(CustomerBasicInfo),
  messages: Schema.Array(TextConciergeMessage),
});
export type TextConciergeConversationDetailResponse =
  typeof TextConciergeConversationDetailResponse.Type;
